import React from "react";
import { ConnectedProps, connect } from "react-redux";
import * as Actions from "us.collection.debtor/actions";
import Common from "us.common";
import { useTranslation } from "react-i18next";
import { DebtorType } from "us.collection.debtor/components/DebtorBasicInfo/Constants";
import {
  isRetainingSelectable,
  getFelidValue,
} from "us.collection.debtor/components/DebtorBasicInfo/Components/DuplicateDebtors/Functions";
import {
  DEBTOR_BASIC_INFO,
  DEBTOR_ADDRESS_INFO,
  PropertyNames,
} from "us.collection.debtor/components/DebtorBasicInfo/Components/DuplicateDebtors/Constants";
import { RootState } from "us.helper/types";
import { BoxIcons, IconTypes, CloseCircleFilled } from "us.icons";
import { DuplicateDebtorProps } from "./Interfaces";
import { getRouteUrl } from "us.helper";
import _ from "lodash";

const { $Button, $Checkbox, $DateLabel, $Tooltip, $Typography } =
  Common.Components;
const { Paragraph } = $Typography;

/**
 * @description - Component to display duplicating debtor info.
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/UnicornBOX/pages/3627384863/Handle+Duplicate+Debtors+UI+Implementation
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 04/08/2024
 */
const DuplicateDebtor: React.FC<DuplicateDebtorProps & PropsFromRedux> = (
  props
) => {
  const { t } = useTranslation(["US.COLLECTION.DEBTOR"]);

  const {
    index,
    debtor,
    retainingDebtor,
    selectedDebtors,
    selectDuplicateDebtors,
    changeRetainingDebtor,
    showDuplicateCount,
  } = props;

  const {
    entRoleId,
    firstName,
    lastName,
    nin,
    debtorType,
    zipCode,
    mismatchingFields,
    isRetainingDebtor,
    isCaseDebtor,
    isBadDebtor,
    lastRegCaseNo,
  } = debtor ?? {};

  const isDebtorSelectable =
    mismatchingFields?.includes("name") ||
    mismatchingFields?.includes("nin") ||
    mismatchingFields?.includes("zipCode") ||
    isBadDebtor ||
    !retainingDebtor;

  const headerContent = (
    <div className="d-flex flex-column">
      <span className={isBadDebtor ? "text-error" : undefined}>
        {t("US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.DEBTOR")} {index + 1}
      </span>
      {isBadDebtor && (
        <span className="text-error font-sm font-weight-normal">
          {t("US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.BAD_DEBTOR")}
        </span>
      )}
      <div className="d-flex flex-column" style={{opacity: isBadDebtor ? 0.5 : 1}}>
        {lastRegCaseNo && (
          <span className="text-muted font-sm">
            {t("US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.LAST_CASE_NO")}:{" "}
            <a
              onClick={() =>
                getRouteUrl.moduleRoute("collection", `/case/${lastRegCaseNo}`)
              }
            >
              {lastRegCaseNo}
            </a>
          </span>
        )}
        {!isCaseDebtor && isRetainingDebtor && (
          <span className="text-muted font-sm font-weight-normal">
            {t(
              "US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.SUGGESTED_RETAIN_DEBTOR"
            )}
          </span>
        )}
        {isCaseDebtor && (
          <span className="text-muted font-sm font-weight-normal">
            {t("US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.CASE_DEBTOR")}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={`mdb-col mdb-col-duplicates ${
        selectedDebtors?.includes(entRoleId) ? "mdb-col-selected" : ""
      }`}
      key={index}
      data-testid="debtor-content"
    >
      <div className="mdb-col-wrap">
        <div className="mdb-row mdb-row-header">
          <$Tooltip
            placement="top"
            title={
              isDebtorSelectable
                ? retainingDebtor && !isBadDebtor
                  ? t(
                      "US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.DEBTOR_CANNOT_BE_MERGED_DUE_TO_THE_MISSING/INCORRECT_INFORMATION_IN_MANDATORY_FIELDS"
                    )
                  : undefined
                : t("US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.SELECT_FOR_MERGE")
            }
          >
            <div>
              {retainingDebtor && (
                <$Checkbox
                  disabled={isDebtorSelectable}
                  checked={selectedDebtors?.includes(entRoleId)}
                  onChange={() =>
                    selectDuplicateDebtors && selectDuplicateDebtors(entRoleId)
                  }
                  className="mdb-checkbox"
                  data-Testid="select-debtor"
                >
                  {headerContent}
                </$Checkbox>
              )}
              {!retainingDebtor && headerContent}
            </div>
          </$Tooltip>

          <div className="mdb-retaining-btn">
            {showDuplicateCount >= 2 && (
              <$Tooltip
                placement="top"
                title={
                  isRetainingSelectable({
                    firstName,
                    lastName,
                    nin,
                    zipCode,
                    isBadDebtor,
                  })
                    ? t(
                        "US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.NOT_ABLE_TO_SET_AS_THE_RETAIN_DEBTOR"
                      )
                    : t(
                        "US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.SET_AS_RETAIN_DEBTOR"
                      )
                }
              >
                <$Button
                  size="small"
                  data-Testid={"select-retaining-debtor"}
                  disabled={isRetainingSelectable({
                    firstName,
                    lastName,
                    nin,
                    zipCode,
                    isBadDebtor,
                  })}
                  icon={
                    <BoxIcons
                      type={IconTypes.BOX_ICON}
                      name={"debtor-select"}
                      viewBox="-10 -3 80 80"
                    />
                  }
                  onClick={() =>
                    changeRetainingDebtor && changeRetainingDebtor(entRoleId)
                  }
                />
              </$Tooltip>
            )}
          </div>
        </div>

        {DEBTOR_BASIC_INFO.map((propertyName, index) => {
          return (
            <div className="mdb-row" key={index}>
              {propertyName == PropertyNames.NAME && (
                <Paragraph ellipsis={{ tooltip: true }} className="m-0">
                  {mismatchingFields?.includes(propertyName) && (
                    <CloseCircleFilled className="mr-1 text-error" />
                  )}
                  {!firstName && !lastName && "-"}
                  {firstName && firstName} {lastName && lastName}
                </Paragraph>
              )}
              {propertyName == PropertyNames.TYPE && (
                <>
                  {mismatchingFields?.includes(propertyName) && (
                    <CloseCircleFilled className="mr-1 text-error" />
                  )}
                  {debtorType == DebtorType.PERSON
                    ? t("US.COLLECTION.CASE:EMPLOYER.PERSON")
                    : t("US.COLLECTION.CASE:EMPLOYER.COMPANY")}
                </>
              )}
              {propertyName == PropertyNames.BIRTH_DATE && (
                <>
                  {mismatchingFields?.includes(propertyName) && (
                    <CloseCircleFilled className="mr-1 text-error" />
                  )}
                  <$DateLabel value={getFelidValue(debtor, propertyName)} />
                </>
              )}
              {propertyName !== PropertyNames.NAME &&
                propertyName !== PropertyNames.TYPE &&
                propertyName !== PropertyNames.BIRTH_DATE && (
                  <Paragraph ellipsis={{ tooltip: true }} className="m-0">
                    {mismatchingFields?.includes(propertyName) && (
                      <CloseCircleFilled className="mr-1 text-error" />
                    )}
                    {getFelidValue(debtor, propertyName)}
                  </Paragraph>
                )}
            </div>
          );
        })}

        <div className="mdb-row mdb-row-header" />

        {DEBTOR_ADDRESS_INFO.map((propertyName, index) => (
          <div className="mdb-row" key={index}>
            <Paragraph ellipsis={{ tooltip: true }} className="m-0">
              {mismatchingFields?.includes(propertyName) && (
                <CloseCircleFilled className="mr-1 text-error" />
              )}
              {getFelidValue(debtor, propertyName)}
            </Paragraph>
          </div>
        ))}
      </div>
    </div>
  );
};

const mapState = (state: RootState) => {
  const { common, domainView, duplicateDebtors } = state;
  const { duplicatingDebtors, selectedDebtors } = duplicateDebtors;
  const { metaData } = domainView;

  return {
    metaData,
    currentLanguage: common.currentLanguage,
    duplicatingDebtors,
    selectedDebtors,
  };
};

const { debtors } = Actions.duplicateDebtors;

const mapDispatch = {
  selectDuplicateDebtors: debtors.select,
  changeRetainingDebtor: debtors.changeRetaining,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DuplicateDebtor);
